import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import type { HfJwtClaims } from '@hf/sharedContract';

const TOKEN_COOKIE = 'hf_token';
const REFRESH_COOKIE = 'hf_refresh';
/** Survives session expiry — skips email 2FA on the next password login while still valid (≤2h inactivity). Explicit logout deletes it. */
const MFA_TRUST_COOKIE = 'hf_mfa_trust';
const EXPIRY_SKEW_SECONDS = 300; // 5 minutes skew to account for clock drift

type SessionClaims = Pick<
  HfJwtClaims,
  | 'sub'
  | 'email'
  | 'source'
  | 'companyKey'
  | 'role'
  | 'firstName'
  | 'lastName'
  | 'datasource'
  | 'isAggregator'
  | 'fundsEnabled'
  | 'managedAccountsEnabled'
  | 'iat'
  | 'exp'
>;

function cookieBase(): Cookies.CookieAttributes {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  return { sameSite: 'strict', path: '/', ...(secure ? { secure: true } : {}) };
}

function cookieOptions(token: string): Cookies.CookieAttributes {
  const options = cookieBase();
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (typeof exp === 'number') options.expires = new Date(exp * 1000);
  } catch {
    // Session cookie if exp cannot be read.
  }
  return options;
}

export function getToken(): string | undefined {
  return Cookies.get(TOKEN_COOKIE);
}

export function getRefreshToken(): string | undefined {
  return Cookies.get(REFRESH_COOKIE);
}

export function getMfaTrustToken(): string | undefined {
  return Cookies.get(MFA_TRUST_COOKIE);
}

export function setTokens(token: string, refreshToken: string, mfaTrustToken?: string): void {
  Cookies.set(TOKEN_COOKIE, token, cookieOptions(token));
  Cookies.set(REFRESH_COOKIE, refreshToken, cookieOptions(refreshToken));
  if (mfaTrustToken) {
    Cookies.set(MFA_TRUST_COOKIE, mfaTrustToken, cookieOptions(mfaTrustToken));
  }
}

export function clearTokens(options?: { revokeMfaTrust?: boolean }): void {
  const base = cookieBase();
  Cookies.remove(TOKEN_COOKIE, base);
  Cookies.remove(REFRESH_COOKIE, base);
  Cookies.remove(TOKEN_COOKIE);
  Cookies.remove(REFRESH_COOKIE);
  if (options?.revokeMfaTrust) {
    Cookies.remove(MFA_TRUST_COOKIE, base);
    Cookies.remove(MFA_TRUST_COOKIE);
  }
}

export function decodeToken(token: string): SessionClaims {
  return jwtDecode<SessionClaims>(token);
}

export function isExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (typeof exp !== 'number') return true;
    const nowSeconds = Date.now() / 1000;
    return exp - EXPIRY_SKEW_SECONDS <= nowSeconds;
  } catch {
    return true;
  }
}
